import React, {
  createContext,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useLocalStorage } from 'react-use';
import {
  ConsoleLogger,
  DefaultDeviceController,
  LogLevel,
} from 'amazon-chime-sdk-js';
import { IS_SERVER_IN_PREVIEW_MODE } from '../config';

type DeviceIdType = 'cameraId' | 'micId' | 'speakerId';
type DeviceListType = 'cameraDevices' | 'micDevices' | 'speakerDevices';
type DeviceSettings = {
  cameraId: string;
  micId: string;
  speakerId: string;
};
type DeviceListMap = {
  cameraDevices: MediaDeviceInfo[];
  micDevices: MediaDeviceInfo[];
  speakerDevices: MediaDeviceInfo[];
};

type GeneratedSetters = {
  setCameraId: (deviceId: string) => void;
  setMicId: (deviceId: string) => void;
  setSpeakerId: (deviceId: string) => void;
};

interface Value extends GeneratedSetters {
  cameraId: string;
  micId: string;
  speakerId: string;
  cameraDevices: MediaDeviceInfo[];
  micDevices: MediaDeviceInfo[];
  speakerDevices: MediaDeviceInfo[];
  isCameraEnabled: boolean;
}

interface Props {
  children: ReactNode;
}

const logger = new ConsoleLogger('MyLogger', LogLevel.WARN);
const deviceController = new DefaultDeviceController(logger);

export const DeviceContext = createContext<Value>({
  cameraId: '',
  micId: '',
  speakerId: '',
  cameraDevices: [],
  micDevices: [],
  speakerDevices: [],
  setCameraId: () => null,
  setMicId: () => null,
  setSpeakerId: () => null,
  isCameraEnabled: false,
});

const defaultDeviceSettings = {
  cameraId: '',
  micId: '',
  speakerId: '',
};

const defaultDeviceLists = {
  cameraDevices: [],
  micDevices: [],
  speakerDevices: [],
};

export function DeviceProvider({ children }: Props) {
  const [isDevicesLoaded, setIsDevicesLoaded] = useState(false);
  const [allDeviceIds, setAllDeviceIds] = useState(new Set());
  const [deviceListMap, setDeviceListMap] =
    useState<DeviceListMap>(defaultDeviceLists);

  const [deviceSettings = defaultDeviceSettings, saveDeviceSettings] =
    useLocalStorage<DeviceSettings>('deviceSettings', defaultDeviceSettings);

  const updateDeviceSettings = useCallback(
    deviceSetting =>
      saveDeviceSettings({ ...deviceSettings, ...deviceSetting }),
    [deviceSettings, saveDeviceSettings],
  );

  const missingDeviceIds = useMemo(() => {
    return Object.keys(defaultDeviceSettings).reduce(
      (acc: string[], key: string) => {
        const deviceIdKey = key as DeviceIdType;
        if (!allDeviceIds.has(deviceSettings[deviceIdKey])) {
          acc.push(key.replace(/Id$/, ''));
        }
        return acc;
      },
      [],
    );
  }, [deviceSettings, allDeviceIds]);

  useEffect(() => {
    if (missingDeviceIds.length) {
      const initialDeviceSettings = missingDeviceIds.reduce(
        (acc: any, device: string): Partial<DeviceSettings> => {
          const deviceList =
            deviceListMap[`${device}Devices` as DeviceListType];
          if (deviceList[0]) {
            const { deviceId } = deviceList[0];
            if (deviceId) {
              acc[`${device}Id`] = deviceId;
            }
          }
          return acc;
        },
        {},
      );
      if (Object.values(initialDeviceSettings).length) {
        updateDeviceSettings(initialDeviceSettings);
      }
    }
  }, [missingDeviceIds, updateDeviceSettings, deviceListMap]);

  useEffect(() => {
    if (IS_SERVER_IN_PREVIEW_MODE) return;
    if (!isDevicesLoaded) {
      const devicePromises = [
        deviceController.listVideoInputDevices(),
        deviceController.listAudioInputDevices(),
        deviceController.listAudioOutputDevices(),
      ];
      Promise.all(devicePromises).then(allDevices => {
        const [cameraDevices, micDevices, speakerDevices] = allDevices;
        const allIds = allDevices.flat().map(({ deviceId }) => deviceId);
        setIsDevicesLoaded(true);
        setAllDeviceIds(new Set(allIds));
        setDeviceListMap({ cameraDevices, micDevices, speakerDevices });
        navigator.mediaDevices.ondevicechange = () => setIsDevicesLoaded(false);
      });
    }
    return () => {
      navigator.mediaDevices.ondevicechange = null;
    };
  }, [isDevicesLoaded, setIsDevicesLoaded]);

  const value: Value = useMemo<Value>(
    () => ({
      ...deviceSettings,
      ...deviceListMap,
      setCameraId: cameraId => updateDeviceSettings({ cameraId }),
      setMicId: micId => updateDeviceSettings({ micId }),
      setSpeakerId: speakerId => updateDeviceSettings({ speakerId }),
      isCameraEnabled: !!deviceSettings.cameraId,
    }),
    [deviceSettings, deviceListMap, updateDeviceSettings],
  );

  return (
    <DeviceContext.Provider value={value}>{children}</DeviceContext.Provider>
  );
}
